import {HttpClient, HttpParams, HttpResponse} from '@angular/common/http';
import {Injectable, inject} from '@angular/core';
import {
  CompoApiService,
  DEFAULT_LOCALE,
  Environment,
  PageApiResponse,
  ProductCustomer,
  PublicShowcase,
} from 'compob2b-libs';
import {Observable, of} from 'rxjs';
import {catchError, map, shareReplay} from 'rxjs/operators';
import {ShowcaseContextService} from '../utils/showcase-context.service';
import {LocaleService} from '../shared/locale/locale.service';

export type ShowcaseExportFormat = 'xlsx' | 'csv' | 'json' | 'xml' | 'pdf';

/** Порядок страницы избранного для `by-ids-paged`: `input` (клиент пресортил по addedAt) | `brand` (ES по brandHeader). */
export type FavoriteOrder = 'input' | 'brand';

/** Тело `POST /product/by-ids-paged` (страница избранного). `page` — 0-based (стандарт Spring). */
interface ByIdsPagedPayload {
  ids: number[];
  page: number;
  limit: number;
  order: FavoriteOrder;
  direction: 'asc' | 'desc';
}

export interface ShowcaseFeatureGroup {
  header: string;
  values: { header: string; value: string }[];
}

/**
 * Тело bulk-запроса: либо явный список товаров (ручной выбор), либо
 * параметры фильтра + список исключённых uid (режим «Выбрать все»).
 * BE должен принять любой из двух вариантов.
 */
export type BulkSelectionPayload =
  | { uids: number[] }
  | { filter: Record<string, string | number | boolean | null>; exclude?: number[] };

@Injectable({providedIn: 'root'})
export class ShowcaseService {
  private readonly api = inject(CompoApiService);
  private readonly http = inject(HttpClient);
  private readonly environment = inject(Environment);
  private readonly ctx = inject(ShowcaseContextService);
  private readonly locale = inject(LocaleService);

  // ---- listing ----------------------------------------------------------

  getProducts(params: HttpParams): Observable<PageApiResponse<ProductCustomer[]>> {
    return this.api.getWithParams<PageApiResponse<ProductCustomer[]>>('/product/filter', params)
      .pipe(map(r => r.data));
  }

  /**
   * Id товаров ПО ТЕКУЩЕМУ ФИЛЬТРУ (bulk «выбрать всё по фильтру → в избранное»).
   * `GET /product/filter-ids` — hash дописывает ShowcaseHashInterceptor. Ответ —
   * `PageCustomerDto<Long>`: весь набор id одним ответом (бэк собирает по окну ES,
   * ≤ кап `showcase.filter-ids.max-total`, иначе 413). Пустой фильтр бэк обслуживает как есть.
   */
  getProductIdsByFilter(params: HttpParams): Observable<number[]> {
    return this.api.getWithParams<PageApiResponse<number[]>>('/product/filter-ids', params)
      .pipe(map(r => r.data?.content ?? []));
  }

  /**
   * Страница избранного — `POST /product/by-ids-paged {ids, page, limit, order, direction}`
   * (hash дописывает ShowcaseHashInterceptor, verb-agnostic). Бэк сортирует и пагинирует
   * ВЕСЬ набор id (кап `showcase.by-ids-paged.max-total`, >кап → 413) и отдаёт ОДНУ страницу
   * тем же контрактом `PageApiResponse<ProductCustomer[]>`, что `/product/filter` — /favorites
   * пагинируется идентично каталогу. `order`: `input` (порядок входа — клиент пресортил id по
   * addedAt) | `brand` (ES-сорт по brandHeader, `direction`). `page` — 1-based в UI (как каталог)
   * → конвертим в 0-based на проводе. Пустой вход обслуживает вызывающий (не шлём: `ids` @NotEmpty).
   */
  getFavoritesPaged(
    ids: number[],
    page: number,
    limit: number,
    order: FavoriteOrder,
    direction: 'asc' | 'desc',
  ): Observable<PageApiResponse<ProductCustomer[]>> {
    const body: ByIdsPagedPayload = {ids, page: Math.max(0, page - 1), limit, order, direction};
    return this.api.post<ByIdsPagedPayload, PageApiResponse<ProductCustomer[]>>('/product/by-ids-paged', body)
      .pipe(map(r => r.data));
  }

  // ---- single product ---------------------------------------------------

  getProduct(id: number): Observable<ProductCustomer> {
    return this.api.get<ProductCustomer>('/product/' + id)
      .pipe(map(r => this.adaptProduct(r.data as ProductCustomer)));
  }

  /** Нормализует showcase-товар (как ProductViewComponent.adaptShowcaseProduct). */
  adaptProduct(product: ProductCustomer | null): ProductCustomer {
    if (!product) {
      return product as ProductCustomer;
    }
    if (!Array.isArray(product.codes)) {
      product.codes = [];
    }
    if (!Array.isArray(product.pictures)) {
      product.pictures = product.picture ? [product.picture] : [];
    }
    (product as any).featureGroups = this.buildFeatureGroups(product);
    if (!Array.isArray(product.certificateList)) {
      product.certificateList = [];
    }
    if (!Array.isArray((product as any).documentList)) {
      (product as any).documentList = [];
    }
    const analogs = (product as any).analogs;
    if (Array.isArray(analogs)) {
      product.analogList = analogs;
    }
    const related = (product as any).relatedProducts;
    if (Array.isArray(related)) {
      product.relatedList = related;
    }
    return product;
  }

  featureGroups(product: ProductCustomer): ShowcaseFeatureGroup[] {
    const groups = (product as any)?.featureGroups;
    return Array.isArray(groups) ? groups : this.buildFeatureGroups(product);
  }

  private buildFeatureGroups(product: any): ShowcaseFeatureGroup[] {
    const groups = Array.isArray(product?.featureGroups) ? product.featureGroups : null;
    if (groups) {
      return groups.map((g: any) => ({
        header: g.header,
        values: (g.features ?? []).map((f: any) => ({header: f.header, value: this.joinValues(f.values)})),
      }));
    }
    const flat = Array.isArray(product?.features) ? product.features : [];
    return [{header: '', values: flat.map((f: any) => ({header: f.header, value: this.joinValues(f.values)}))}];
  }

  private joinValues(values: any[]): string {
    if (!Array.isArray(values)) {
      return '';
    }
    return values.map(v => v?.header).filter((h: any) => h !== null && h !== undefined && h !== '').join(', ');
  }

  // ---- export / download URLs ------------------------------------------

  private productUrl(id: number, suffix: string): string | null {
    const hash = this.ctx.hash;
    if (!hash || id == null) {
      return null;
    }
    return this.withLocale(`${this.environment.serverApi}/showcase/${hash}/product/${id}/${suffix}`);
  }

  /**
   * Прокидывает текущую локаль в download-URL витрины. Браузер открывает эти
   * ссылки напрямую (не XHR), поэтому Accept-Language от SPA до них не доходит —
   * локаль резолвится бэком из query-параметра `?locale=`. Для дефолтной локали
   * ничего не добавляем, чтобы не плодить лишние параметры.
   */
  private withLocale(url: string): string {
    const locale = this.locale.current;
    if (locale === DEFAULT_LOCALE) {
      return url;
    }
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}locale=${locale}`;
  }

  exportUrl(id: number, format: ShowcaseExportFormat): string | null {
    return this.productUrl(id, `export?format=${format}`);
  }

  mediaZipUrl(id: number): string | null {
    return this.productUrl(id, 'media-zip');
  }

  documentsZipUrl(id: number): string | null {
    return this.productUrl(id, 'documents-zip');
  }

  // ---- bulk export / media-zip -----------------------------------------
  // Один POST → один ZIP. URL без `/showcase/{hash}/...`: префикс добавит
  // ShowcaseHashInterceptor (как и для остальных API-вызовов витрины).

  bulkExport(payload: BulkSelectionPayload, format: ShowcaseExportFormat): Observable<HttpResponse<Blob>> {
    const url = this.withLocale(`${this.environment.serverApi}/product/bulk-export?format=${format}`);
    return this.http.post(url, payload, {responseType: 'blob' as const, observe: 'response' as const});
  }

  bulkMediaZip(payload: BulkSelectionPayload): Observable<HttpResponse<Blob>> {
    const url = this.withLocale(`${this.environment.serverApi}/product/bulk-media-zip`);
    return this.http.post(url, payload, {responseType: 'blob' as const, observe: 'response' as const});
  }

  /** ZIP всех документов выбранных товаров (паспорта, инструкции, сертификаты). */
  bulkDocumentsZip(payload: BulkSelectionPayload): Observable<HttpResponse<Blob>> {
    const url = this.withLocale(`${this.environment.serverApi}/product/bulk-documents-zip`);
    return this.http.post(url, payload, {responseType: 'blob' as const, observe: 'response' as const});
  }

  // ---- метаданные публичной витрины (логотип, имя, описание) -----------
  // GET /api/v1/showcase/{hash} — публичный эндпоинт BE. Пути под `/showcase/`
  // bypass-ятся ShowcaseHashInterceptor (URL содержит hash явно).

  /** Кэш ответа /showcase/{hash} — один HTTP-запрос на жизнь приложения,
   *  расшариваем между layout-валидацией и header (логотип/имя). */
  private showcaseInfo$?: Observable<PublicShowcase | null>;

  getShowcaseInfo(): Observable<PublicShowcase | null> {
    if (this.showcaseInfo$) { return this.showcaseInfo$; }
    const hash = this.ctx.hash;
    if (!hash) {
      this.showcaseInfo$ = of(null);
      return this.showcaseInfo$;
    }
    this.showcaseInfo$ = this.http.get<{data?: PublicShowcase} | PublicShowcase>(`${this.environment.serverApi}/showcase/${hash}`)
      .pipe(
        map(r => (r as any)?.data ?? (r as PublicShowcase) ?? null),
        catchError(() => of(null)),
        shareReplay(1),
      );
    return this.showcaseInfo$;
  }
}
